// hooks/useAppointmentTrends.ts
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { getAppointmentTrends, PatientManagementDataPayload } from '../../services';
import { colors } from '../../constants';
import { AppointmentTrends } from 'data';

export const useAppointmentTrends = (payload: PatientManagementDataPayload) => {
  const { t } = useTranslation();

  const query = useQuery<AppointmentTrends>({
    queryKey: ['appointment-trends', payload.startTimestamp, payload.endTimestamp],
    queryFn: async () => {
      const result = await getAppointmentTrends(payload);

      return {
        completedAppointments: result?.completedAppointments?.aggregate?.count ?? 0,
        approvedAppointments: result?.approvedAppointments?.aggregate?.count ?? 0,
        pendingAppointments: result?.pendingAppointments?.aggregate?.count ?? 0,
        rejectedAppointments: result?.rejectedAppointments?.aggregate?.count ?? 0
      } as AppointmentTrends;
    },
    enabled: !!payload?.startTimestamp && !!payload?.endTimestamp,
    staleTime: 5 * 60 * 1000,
    retry: 1
  });

  const isEmpty = useMemo(() => {
    if (!query.data) return true;
    return Object.values(query.data).every((val) => val === 0);
  }, [query.data]);

  const chartData = useMemo(
    () => ({
      labels: [t('completed'), t('approved'), t('pending'), t('rejected')],
      datasets: [
        {
          label: isEmpty ? t('noRecordFound') : t('patientsManagement.bookings'),
          data: isEmpty
            ? [0.00001]
            : [
                query?.data?.completedAppointments ?? 0,
                query?.data?.approvedAppointments ?? 0,
                query?.data?.pendingAppointments ?? 0,
                query?.data?.rejectedAppointments ?? 0
              ],
          backgroundColor: isEmpty
            ? [colors.grayLighter]
            : [colors.info, colors.iconSearch, colors.purpleLight, colors.pinkDark],
          borderColor: isEmpty
            ? [colors.grayLighter]
            : [colors.info, colors.iconSearch, colors.purpleLight, colors.pinkDark],
          borderWidth: 5
        }
      ],
      text: '',
      subText: ''
    }),
    [query.data, isEmpty, t]
  );

  return {
    ...query,
    isEmpty,
    chartData
  };
};
